import { useState } from "react";
import PropTypes from "prop-types";
import { Modal, Button, Input, Typography } from "antd";
import { AlertTriangle } from "lucide-react";

const { Text } = Typography;

/**
 * Reusable confirmation dialog component
 * 
 * @param {boolean} open - Controls dialog visibility
 * @param {function} onClose - Called when dialog should close
 * @param {function} onConfirm - Called when user confirms action
 * @param {string} title - Dialog title
 * @param {string} message - Main message/description
 * @param {string} confirmText - Text for confirm button (default: "Подтвердить")
 * @param {string} cancelText - Text for cancel button (default: "Отмена")
 * @param {boolean} requireTextConfirmation - If true, requires typing confirmValue to enable confirm button
 * @param {string} confirmValue - Value user must type to confirm (when requireTextConfirmation is true)
 * @param {string} variant - Visual variant: "danger" or "warning" (default: "danger")
 */
export default function ConfirmDialog({
    open,
    onClose,
    onConfirm,
    title = "Подтверждение",
    message,
    confirmText = "Подтвердить",
    cancelText = "Отмена",
    requireTextConfirmation = false,
    confirmValue = "",
    variant = "danger",
    children,
}) {
    const [inputValue, setInputValue] = useState("");
    const [isLoading, setIsLoading] = useState(false);

    const handleConfirm = async () => {
        setIsLoading(true);
        try {
            await onConfirm();
            setInputValue("");
            onClose();
        } catch (error) {
            console.error("Confirmation error:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleClose = () => {
        if (!isLoading) {
            setInputValue("");
            onClose();
        }
    };

    const isConfirmEnabled = requireTextConfirmation
        ? inputValue === confirmValue && !isLoading
        : !isLoading;

    const variantStyles = {
        danger: {
            iconBg: "bg-red-100",
            iconColor: "text-red-600",
            alertBg: "bg-red-50",
            alertBorder: "border-red-200",
            alertText: "text-red-700",
            dangerColor: "danger",
        },
        warning: {
            iconBg: "bg-amber-100",
            iconColor: "text-amber-600",
            alertBg: "bg-amber-50",
            alertBorder: "border-amber-200",
            alertText: "text-amber-700",
            dangerColor: "default",
        },
    };

    const styles = variantStyles[variant] || variantStyles.danger;

    return (
        <Modal
            open={open}
            onCancel={handleClose}
            closable={!isLoading}
            maskClosable={!isLoading}
            footer={[
                <Button key="cancel" onClick={handleClose} disabled={isLoading}>
                    {cancelText}
                </Button>,
                <Button
                    key="confirm"
                    danger={variant === "danger"}
                    type="primary"
                    onClick={handleConfirm}
                    disabled={!isConfirmEnabled}
                    loading={isLoading}
                >
                    {isLoading ? "Выполнение..." : confirmText}
                </Button>,
            ]}
            title={
                <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 ${styles.iconBg} rounded-full flex items-center justify-center flex-shrink-0`}>
                        <AlertTriangle size={18} className={styles.iconColor} />
                    </div>
                    <span>{title}</span>
                </div>
            }
        >
            <div className="space-y-4 py-2">
                {message && <Text type="secondary">{message}</Text>}

                {children && (
                    <div className={`p-4 ${styles.alertBg} border ${styles.alertBorder} rounded-lg`}>
                        <div className={`text-sm ${styles.alertText}`}>{children}</div>
                    </div>
                )}

                {requireTextConfirmation && (
                    <div>
                        <label className="block text-sm font-medium text-zinc-700 mb-2">
                            Для подтверждения введите:{" "}
                            <Text strong>{confirmValue}</Text>
                        </label>
                        <Input
                            value={inputValue}
                            onChange={(e) => setInputValue(e.target.value)}
                            placeholder={`Введите "${confirmValue}"`}
                            disabled={isLoading}
                            autoFocus
                        />
                    </div>
                )}
            </div>
        </Modal>
    );
}

ConfirmDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onConfirm: PropTypes.func.isRequired,
    title: PropTypes.string,
    message: PropTypes.string,
    confirmText: PropTypes.string,
    cancelText: PropTypes.string,
    requireTextConfirmation: PropTypes.bool,
    confirmValue: PropTypes.string,
    variant: PropTypes.oneOf(["danger", "warning"]),
    children: PropTypes.node,
};
